import React, { useEffect, useState, useMemo } from 'react';
import { FormDialog, InputField, MuiBox, Typography, SelectDropdown } from '@kis-technologies/kis-ui';
import { Hardware, CreateHardwarePayload } from 'store/hardware';
import { getSortedList } from 'utils/stringFormat';
import {
  getHardwareInitialForm,
  HardwareTypeList,
  hardwareValidationSchema,
  getHasEmptyInput,
  getHasInvalidInput,
  isValidJSON
} from 'views/KiosksView/utils';
import { HardwareType, Mode } from 'views/KiosksView/types';
import { INVALID, VALID } from 'views/KiosksView/constants';
import { useKioskFormStyles } from '../styled';

export type HardwarePayload = CreateHardwarePayload & {
  details: Record<string, unknown>;
  // Set when editing existing hardware
  id?: string;
};

interface HardwareFormProps {
  mode: Mode;
  initialData?: Hardware;
  onClose: () => void;
  onSubmit: (payload: HardwarePayload) => void;
}

type HardwareFormData = ReturnType<typeof getHardwareInitialForm> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  details?: any;
};

export const HardwareForm: React.FC<HardwareFormProps> = ({ mode, initialData, onClose, onSubmit }) => {
  const formClasses = useKioskFormStyles();

  const isEdit = mode === 'edit';
  const initialForm = getHardwareInitialForm();

  const [formData, setFormData] = useState<HardwareFormData>(initialForm);
  const [validation, setValidation] = useState<{ [x: string]: string }>({});
  const [validationMessage, setValidationMessage] = useState<{ [x: string]: string | null }>({});
  const [selectedType, setSelectedType] = useState<HardwareType | null>(null);

  const sortedTypes = getSortedList(HardwareTypeList as HardwareType[], 'name');

  const hasEmptyInput = useMemo(() => getHasEmptyInput(formData), [formData]);
  const hasInvalidInput = useMemo(() => getHasInvalidInput(validation), [validation]);

  const buttonDisabled = hasEmptyInput || hasInvalidInput || !selectedType;

  /**
   * PREFILL DATA (EDIT MODE)
   */
  useEffect(() => {
    if (isEdit && initialData) {
      setFormData({
        ...initialForm,
        ...initialData
      });

      const typeItem = sortedTypes.find((t) => t.code === initialData.type);
      setSelectedType(typeItem || null);
    }
  }, [initialData]);

  /**
   * INPUT CHANGE
   */
  const onInputChange = (key: keyof HardwareFormData, e: React.ChangeEvent<HTMLInputElement>) => {
    if (validation[key] === INVALID) {
      setValidation({ ...validation, [key]: VALID });
      setValidationMessage({ ...validationMessage, [key]: null });
    }

    setFormData({
      ...formData,
      [key]: e.target.value
    });
  };

  /**
   * TYPE CHANGE
   */
  const onTypeChange = (item: HardwareType) => {
    setSelectedType(item);

    setFormData({
      ...formData,
      type: item.code
    });
  };

  /**
   * INPUT BLUR VALIDATION
   */
  const onInputBlur = async (key: keyof HardwareFormData, e: React.ChangeEvent<HTMLInputElement>) => {
    const trimmed = e.target.value.trim();

    setFormData({
      ...formData,
      [key]: trimmed
    });

    let invalidMessage = null;

    if (trimmed.length) {
      const result = await hardwareValidationSchema.validate({ [key]: trimmed }).catch((err) => err);

      invalidMessage = result?.message;
    }

    setValidation({
      ...validation,
      [key]: invalidMessage ? INVALID : VALID
    });

    setValidationMessage({
      ...validationMessage,
      [key]: invalidMessage
    });
  };

  const validate = (key: keyof HardwareFormData) => validationMessage[key];

  /**
   * SUBMIT
   */
  const handleSubmit = () => {
    if (buttonDisabled) return;

    let parsedDetails: Record<string, unknown> = {};

    if (typeof formData.details === 'string' && formData.details) {
      if (!isValidJSON(formData.details)) {
        setValidationMessage({
          ...validationMessage,
          details: 'Invalid JSON format'
        });
        return;
      }

      parsedDetails = JSON.parse(formData.details);
    }

    const payload: HardwarePayload = {
      name: formData.name,
      type: formData.type,
      code: formData.code,
      details: parsedDetails,
      ...(isEdit && initialData?.id && { id: initialData.id })
    };

    onSubmit(payload);
  };

  return (
    <FormDialog
      open
      title={isEdit ? 'Edit Hardware' : 'Create Hardware'}
      confirmButtonText={isEdit ? 'Save' : 'Create'}
      cancelButtonText="Cancel"
      cancelButtonAction={onClose}
      confirmButtonAction={handleSubmit}
      confirmButtonDisabled={buttonDisabled}
    >
      <MuiBox mt="14px">
        {/* NAME */}
        <MuiBox className={formClasses.formOverlayInputBox}>
          <InputField
            label="Name"
            size="small"
            fullWidth
            sx={{ width: '262px', height: '45px' }}
            value={formData.name || ''}
            onChange={(e) => onInputChange('name', e)}
            onBlur={(e) => onInputBlur('name', e)}
            helperText={validate('name')}
            error={validation.name === INVALID}
          />
        </MuiBox>

        {/* TYPE */}
        <MuiBox className={formClasses.formOverlayInputBox}>
          <Typography display="flex" justifyContent="space-between" className={formClasses.formDropdownLabel}>
            Type
          </Typography>
          <SelectDropdown
            placeholder="Select type"
            items={sortedTypes}
            itemToString={(item) => item?.name || ''}
            selectedItem={selectedType}
            onChange={(item: HardwareType) => onTypeChange(item)}
            radius="round"
            width="262px"
            sx={{
              boxShadow: 'none',
              height: '45px',
              '& .MuiSelect-select': {
                color: selectedType ? '#000' : '#9E9E9E'
              }
            }}
          />
        </MuiBox>

        {/* CODE */}
        <MuiBox className={formClasses.formOverlayInputBox}>
          <InputField
            label="Code"
            size="small"
            fullWidth
            sx={{ width: '262px', height: '45px' }}
            value={formData.code || ''}
            onChange={(e) => onInputChange('code', e)}
            onBlur={(e) => onInputBlur('code', e)}
            helperText={validate('code')}
            error={validation.code === INVALID}
          />
        </MuiBox>

        {/* DETAILS */}
        <MuiBox className={formClasses.formOverlayInputBox}>
          <InputField
            multiline
            label="Details"
            size="small"
            rows={16}
            fullWidth
            sx={{ width: '522px', height: '400px', borderRadius: '25px' }}
            value={
              typeof formData.details === 'object' ? JSON.stringify(formData.details) : String(formData.details || '')
            }
            onChange={(e) => onInputChange('details', e)}
            helperText={validationMessage.details || ''}
            error={Boolean(validationMessage.details)}
          />
        </MuiBox>
      </MuiBox>
    </FormDialog>
  );
};
